import Device from '../Device';
import UserContentItem from '../UserContentItem';
import {
  getDocumentsForMarkers,
  getMarkersWithDescendentsForMarkers,
  getPercentageOfBookForDocuments,
} from 'utilities';

class UserContentOfflineSession extends UserContentItem {
  static typeName = 'offlineSessions';

  #device;

  #relativePaths = [];
  #relativePathsByDocument = new Map();

  #documents = [];
  #markers = [];
  #wholeBook = false;

  constructor(properties = {}) {
    super(...arguments);

    const book = properties.book;
    this.#wholeBook = properties.wholeBook;

    if (!properties.deviceInfo) {
      throw new Error('deviceInfo is required');
    }

    this.#device = new Device(properties.deviceInfo);

    // Gather markers
    const markerIndices = [...new Set(properties.markerIndices ?? [])];
    markerIndices.sort((a, b) => a - b);
    this.#markers = getMarkersWithDescendentsForMarkers(
      book.markersByIndices(markerIndices),
    );

    // Gather documents
    this.#documents = properties.wholeBook
      ? book.documents
      : getDocumentsForMarkers(this.#markers);

    // Gather relativePathsByDocument
    const documentResources = properties.documentResources ?? {};
    for (let documentIndex of Object.keys(documentResources)) {
      const document = book.documents[documentIndex];
      if (this.documents.includes(document)) {
        // Remove the document html resource path from the list.
        // We use a special /spine/ url for the document html.
        const paths = documentResources[documentIndex].filter(
          (path) => path !== document.path,
        );

        this.#relativePathsByDocument.set(document, paths);
      }
    }

    // Gather relative paths
    this.#relativePaths = [
      ...new Set(Array.from(this.relativePathsByDocument.values()).flat()),
    ];
  }

  get device() {
    return this.#device;
  }

  get deviceInfo() {
    return {
      uuid: this.device.uuid,
      description: this.device.description,
      nthOfKind: this.device.nthOfKind,
    };
  }

  get relativePaths() {
    return this.#relativePaths;
  }

  get relativePathsByDocument() {
    return this.#relativePathsByDocument;
  }

  getAbsolutePaths(bookContentBasePath) {
    const absolutePathsArray = [];
    const absolutePathsByDocument = new Map();

    for (let document of this.documents) {
      const pathsInDocument = (
        this.relativePathsByDocument.get(document) ?? []
      ).map((relativePath) => `${bookContentBasePath}${relativePath}`);

      absolutePathsArray.push(...pathsInDocument);
      absolutePathsByDocument.set(document, pathsInDocument);
    }

    return {
      absolutePaths: new Set(absolutePathsArray),
      absolutePathsByDocument,
    };
  }

  get documents() {
    return this.#documents;
  }

  get markers() {
    return this.#markers;
  }

  get markerIndices() {
    return this.markers.map((marker) => marker.index);
  }

  get percentage() {
    return getPercentageOfBookForDocuments(this.documents);
  }

  get wholeBook() {
    return this.#wholeBook;
  }

  serialize() {
    const { markerIndices, deviceInfo, documentResources, wholeBook } = this;
    return {
      ...super.serialize(),

      deviceInfo,
      markerIndices,
      documentResources,
      wholeBook,
    };
  }

  async persist({ updateMutation, deleteMutation }) {
    const { deviceInfo, markerIndices, wholeBook } = this;
    const epubId = this.book.pk;

    if (this.shouldDelete) {
      await deleteMutation({
        variables: {
          id: this.pk,
        },
      });

      return null;
    }

    const {
      data: { createOrUpdateOfflineSession },
    } = await updateMutation({
      variables: {
        input: {
          epubId,
          deviceUuid: deviceInfo.uuid,
          markerIndices: wholeBook ? [] : markerIndices,
          wholeBook,
        },
      },
    });

    return createOrUpdateOfflineSession?.offlineSession?.id;
  }
}

export default UserContentOfflineSession;
